// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Sprint 63.31.0 (#444, #445) — chemical traceability declarations ─────────
//
// A view over form instances of the templates an installed pack links to its
// chemical-traceability deadline rows (`linked_form_id`), discovered by the
// rows' `event_type` and never by a template key, framework code or instrument
// (R-46). No new collection: the declaration IS the form instance; this view
// reads it, dates it by the clocks the pack declares, and records the links a
// person makes to incidents and sector licences.
//
// The platform assigns no identification code: every code on these records is
// the organisation's own copy of what the System issued, held as free text.
/** Deadline `event_type` of the incident, shortfall, loss, destruction or safety-risk declaration clock. */
export const CHEMICAL_TRACEABILITY_DECLARATION_EVENT_TYPE = 'chemical_traceability_incident_declaration';
/** Deadline `event_type` of the code-termination windows (explanation, restoration). */
export const CHEMICAL_TRACEABILITY_TERMINATION_EVENT_TYPE = 'chemical_traceability_code_termination';
/** The guidance tag under which a chemical-traceability pack authors its boundary statement. */
export const CHEMICAL_TRACEABILITY_BOUNDARY_TAG = 'platform-scope';
/** The two kinds of record the view reads, one per event type. */
export const CHEMICAL_TRACEABILITY_RECORD_KINDS = ['declaration', 'termination'];
/** Which event type each record kind is read by. */
export const CHEMICAL_TRACEABILITY_EVENT_TYPE_BY_KIND = {
    declaration: CHEMICAL_TRACEABILITY_DECLARATION_EVENT_TYPE,
    termination: CHEMICAL_TRACEABILITY_TERMINATION_EVENT_TYPE,
};
/**
 * The two anchors of the declaration clock, as the declaration template's own
 * field ids. BOTH are shown and NEITHER is chosen (owner decision 2026-10-11):
 * the text names discovery or occurrence and does not say which governs.
 */
export const CHEMICAL_DECLARATION_ANCHOR_FIELDS = ['occurred_at', 'discovered_at'];
/**
 * The field of the termination record each termination window runs from, by
 * whose clock the row declares it to be (`obligation_owner`): the
 * organisation's explanation window from the day the System recorded sending
 * the notice; the managing body's restoration window from the verification
 * result. A template that does not declare the field yields no date.
 */
export const CHEMICAL_TERMINATION_ANCHOR_FIELD_BY_OWNER = {
    organization: 'notice_recorded_on',
    authority: 'verification_result_on',
};
/**
 * Sprint 63.31.0 browser verification (UC-VN-50 S10) — the field attribute by
 * which a pack marks the field of a declaration-log or termination template
 * that holds the code the record is about (the declaration's identification
 * code or lot, the terminated code): `record_code: true`. The view names a row
 * by the first such field, in template order, that the record holds, falling
 * back to the record's own reference; the value is the organisation's copy of
 * what the System issued, shown as recorded. The platform names no field id
 * here (R-46); `validate:packs` holds the attribute (`RECORD_CODE_*`).
 */
export const PACK_FIELD_RECORD_CODE_KEY = 'record_code';
/**
 * Gate E12.5 pass 1 (P2-L4) — the option attribute by which a pack says a
 * clock does not apply to a record that carries that option: an array of the
 * `deadline_id`s it disapplies. A termination's explanation window, for
 * example, does not exist where the termination follows a competent body's
 * written request or decision; the pack puts this attribute on the option that
 * records that ground. The platform names no field, option or deadline here
 * (R-46): it reads the attribute off whichever recorded option carries it.
 */
export const PACK_OPTION_DISAPPLIES_DEADLINES_KEY = 'disapplies_deadline_ids';
//# sourceMappingURL=chemical-traceability.js.map